// RESPONSIVE BREAKPOINTS
export const breakpoint = {
  mobile: 768,
};

export const deviceType = {
  mobile: `(max-width: ${breakpoint.mobile}px)`,
  desktop: `(min-width: ${breakpoint.mobile}px)`,
};

// CALENDAR UTILITY FUNCTIONS
export const randomDayData = () => {
  const min = 10000;
  const max = 15000;
  const randomNum = Math.floor(Math.random() * (max - min + 1) + min);

  return {
    price: `₹${randomNum}`,
    class: randomNum <= 12000 ? "greenText" : "redText",
    isSoldOut: randomNum > 12000 && randomNum < 13000,
    availableOnPreviousDate: randomNum >= 13000 && randomNum <= 13500,
  };
};

export const dayAdditionalData = new Array(31)
  .fill(0)
  .map(() => randomDayData());

export const MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sept",
  "Oct",
  "Nov",
  "Dec",
];

export const SHORT_WEEK_DAYS = [
  "Sun",
  "Mon",
  "Tue",
  "Wed",
  "Thurs",
  "Fri",
  "Sat",
];

export const LONG_WEEK_DAYS = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
];

export const mfActiveTypeMatch = {
  0: "Option 1",
  1: "Option 2",
  2: "Option 3",
};

// FOR DISPAYING INSIDE THE CALENDAR
export const calendarLabelFormatter = (dateObj: any) => {
  const indiaNow = new Date(Date.now() + 330 * 60 * 1000);
  return dateObj
    ? `${dateObj.getDate()} ${MONTHS[dateObj.getMonth()]}' ${dateObj
        .getFullYear()
        .toString()
        .slice(-2)}`
    : `${indiaNow.getUTCDate()} ${MONTHS[indiaNow.getUTCMonth()]}' ${indiaNow
        .getUTCFullYear()
        .toString()
        .slice(-2)}`;
};

export const insertBrOnNewLine = (text: string) => {
  return text.replace(/\n/g, "<br/>");
};

insertBrOnNewLine("hello he\nyello");

export const generateSourceSetImageUrl = (
  cdnUrlWidths: Array<number>,
  imgUrl: string,
  width: number
) => {
  let imgWidths = cdnUrlWidths;
  if (!imgWidths || !Array.isArray(imgWidths) || imgWidths.length === 0)
    imgWidths = [width, width * 2, width * 3];

  return imgWidths
    .map((width) => `${imgUrl}?im=Resize=(${width}) ${width}w`)
    .join(",");
};

/**
 * India timezone helpers.
 *
 * Insurance flows treat calendar dates as "India dates" (Asia/Kolkata) irrespective of
 * the user's device timezone. To keep UI consistent across timezones:
 * - store/share dates as unix seconds representing **00:00 Asia/Kolkata**
 * - render dates in UI using a Date instance whose **Y/M/D equals the India date**
 */
const INDIA_TZ_OFFSET_MINUTES = 330; // Asia/Kolkata is UTC+05:30 (no DST)
const INDIA_TZ_OFFSET_MS = INDIA_TZ_OFFSET_MINUTES * 60 * 1000;

type YMD = { year: number; monthIndex: number; day: number };

const getIndiaYmdFromNow = (): YMD => {
  const shifted = new Date(Date.now() + INDIA_TZ_OFFSET_MS);
  return {
    year: shifted.getUTCFullYear(),
    monthIndex: shifted.getUTCMonth(),
    day: shifted.getUTCDate(),
  };
};

const getIndiaYmdFromUnix = (unixSeconds: number): YMD => {
  const shifted = new Date(unixSeconds * 1000 + INDIA_TZ_OFFSET_MS);
  return {
    year: shifted.getUTCFullYear(),
    monthIndex: shifted.getUTCMonth(),
    day: shifted.getUTCDate(),
  };
};

export const getIndiaTodayDate = (): Date => {
  const { year, monthIndex, day } = getIndiaYmdFromNow();
  // Local Date object, but with India Y/M/D.
  return new Date(year, monthIndex, day);
};

export const convertDateToUnix = (date: Date) => {
  if (!(date instanceof Date) || isNaN(date.getTime())) return 0;
  // Interpret the given calendar date as an India date (00:00 Asia/Kolkata).
  const y = date.getFullYear();
  const m = date.getMonth();
  const d = date.getDate();
  const utcMs = Date.UTC(y, m, d, 0, 0, 0, 0) - INDIA_TZ_OFFSET_MS;
  return Math.floor(utcMs / 1000);
};

export const convertUnixToDate = (unix: number) => {
  if (!unix || typeof unix !== "number" || Number.isNaN(unix))
    return new Date();
  const { year, monthIndex, day } = getIndiaYmdFromUnix(unix);
  // Local Date object, but with India Y/M/D.
  return new Date(year, monthIndex, day);
};

export enum DATE_FORMATS {
  "DDD" = "ddd",
  "YY" = "yy",
  "YYYY" = "yyyy",
  "D MMM" = "d MMM",
  "DD/MM/YYYY" = "dd/MM/yyyy",
  "DD MMM YY" = "dd MMM'yy",
}

const MONTHS_SHORT = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

export const formatDate = (date: Date, format: DATE_FORMATS) => {
  try {
    date = new Date(date);
    switch (format) {
      case DATE_FORMATS["DD MMM YY"]:
        return `${date.getDate()} ${MONTHS_SHORT[date.getMonth()]}'${date
          .getFullYear()
          .toString()
          .substr(-2)}`;
      default:
        return;
    }
  } catch (error) {
    console.log("Error in formatting given date", date, format);
  }
  return;
};

/* istanbul ignore next */
export const isOnServer = () => {
  if (typeof window === "undefined") {
    return true;
  }
  return false;
};

export const TRIPMONEY_MMT_DOMAIN_NAMES = [
  "tripmoney.makemytrip.com",
  "www.makemytrip.com",
];

/* istanbul ignore next */
export const isHostMakemytrip = () => {
  if (isOnServer()) return false;
  const url = new URL(window.location.href);
  return TRIPMONEY_MMT_DOMAIN_NAMES.includes(url.hostname);
};

// COUNTRY DATA UTILITIES
interface Country {
  code: string;
  isSelected?: boolean;
  isDisabled?: boolean;
  [key: string]: any;
}

interface UpcomingTrip {
  countryCode?: string;
  [key: string]: any;
}

/**
 * Generates initial country data with selected countries based on URL parameters or upcoming trip
 * @param countryList - Array of country objects from the travel form info
 * @param urlCountryCodes - Array of country codes from URL parameters
 * @param upcomingTrip - Upcoming trip data containing countryCode
 * @returns Array of countries with isSelected and isDisabled properties
 */
export const getInitialCountryData = (
  countryList: any[],
  urlCountryCodes: string[] = [],
  upcomingTrip?: UpcomingTrip,
  prePopulatedData?: any,
  isCommonLandingRevamp?: boolean,
  selectedTripOption?: any
): Country[] => {
  if (!countryList || !Array.isArray(countryList)) {
    return [];
  }

  // Initialize all countries with isSelected: false and isDisabled: false
  let initialCountryData = countryList.map((country: any) => ({
    ...country,
    isSelected: false,
    isDisabled: false,
  }));

  // If URL has country codes, mark those as selected
  if (urlCountryCodes.length > 0) {
    if (isCommonLandingRevamp && selectedTripOption === "MULTITRIP") {
      const multiCountryCode =
        urlCountryCodes.includes("USA") ||
        urlCountryCodes.includes("CAN") ||
        urlCountryCodes.includes("WW")
          ? ["WW"]
          : ["WWEUC"];
      initialCountryData = initialCountryData.map((country: any) => {
        if (multiCountryCode.includes(country.code)) {
          return { ...country, isSelected: true };
        }
        return { ...country };
      });
    } else {
      initialCountryData = initialCountryData.map((country: any) => {
        if (urlCountryCodes.includes(country.code)) {
          return { ...country, isSelected: true };
        }
        return { ...country };
      });
    }
  }
  // If no URL country codes but upcoming trip has a country, mark that as selected
  else if (
    prePopulatedData?.countryCode &&
    prePopulatedData.countryCode.length > 0
  ) {
    initialCountryData = initialCountryData.map((country: any) => {
      if (prePopulatedData.countryCode.includes(country.code)) {
        return { ...country, isSelected: true };
      }
      return { ...country };
    });
  } else if (upcomingTrip?.countryCode) {
    initialCountryData = initialCountryData.map((country: Country) => {
      if (country.code === upcomingTrip.countryCode) {
        return { ...country, isSelected: true };
      }
      return { ...country };
    });
  }

  return initialCountryData;
};

/**
 * Processes country data based on query parameters and upcoming trip data
 * This function is server-side compatible and can be used in both client and server contexts
 * @param countryList - Array of country objects from the travel form info
 * @param queryParams - Query parameters object containing countries, startDate, endDate, segment, etc.
 * @param upcomingTrip - Upcoming trip data containing countryCode
 * @returns Object containing processed countryData and popularCountryData
 */
export const processCountryDataFromQuery = (
  countryList: any[],
  queryParams?: {
    countries?: string;
    startDate?: string;
    endDate?: string;
    segment?: string;
    [key: string]: any;
  },
  upcomingTrip?: UpcomingTrip,
  countryCode?: string
): {
  countryData: any[];
  popularCountryData: any[];
} => {
  if (!countryList || !Array.isArray(countryList)) {
    return {
      countryData: [],
      popularCountryData: [],
    };
  }

  const selectedCountries = queryParams?.countries || countryCode;
  let selectedCountryCodes: string | string[] | undefined;

  if (selectedCountries) {
    selectedCountryCodes = selectedCountries.split(",");
  } else if (upcomingTrip?.countryCode) {
    selectedCountryCodes = Array.isArray(upcomingTrip.countryCode)
      ? upcomingTrip.countryCode
      : [upcomingTrip.countryCode];
  }

  // Map countries with selected state
  let totalCountryData = countryList.map((data: any) => {
    if (
      selectedCountryCodes &&
      Array.isArray(selectedCountryCodes) &&
      selectedCountryCodes.includes(data.code)
    ) {
      return { ...data, isSelected: true };
    }
    return { ...data };
  });

  // Handle exclusive country selection
  const isExclusiveCountrySelected = totalCountryData.find(
    (item: any) => item.isExclusiveSelection && item.isSelected
  );

  totalCountryData = totalCountryData.map((country: any) => {
    if (isExclusiveCountrySelected) {
      return country.isExclusiveSelection
        ? { ...country }
        : { ...country, isSelected: false, isDisabled: true };
    } else if (totalCountryData.some((item: any) => item.isSelected)) {
      return country.isExclusiveSelection
        ? { ...country, isDisabled: true }
        : { ...country };
    }
    return { ...country };
  });

  // Handle region codes
  const selectedRegionCodes = totalCountryData
    .filter((data: any) => data.isSelected && data.type === "REGION")
    .map((data: any) => data.code);

  selectedRegionCodes.forEach((code: string) => {
    totalCountryData = totalCountryData.map((country: any) => {
      if (country.regionCodes && country.regionCodes.includes(code)) {
        return { ...country, isDisabled: true };
      }
      return { ...country };
    });
  });

  // Filter and sort popular country data
  let popularCountryData = totalCountryData.filter(
    (country: any) => country.isSelected || country.isPopular
  );

  popularCountryData = [
    ...(popularCountryData || []).filter(
      (country: any) => country.isSelected && !country.isDisabled
    ),
    ...(popularCountryData || []).filter((country: any) => country.isDisabled),
    ...(popularCountryData || []).filter(
      (country: any) => !country.isSelected && !country.isDisabled
    ),
  ];

  return {
    countryData: totalCountryData,
    popularCountryData: popularCountryData,
  };
};
